import { getFloatNumberRoundValue } from '@utils_iqfu/getFloatNumberRoundValue';

import { AiAppraisalReportAreaStatType, AiAppraisalReportUsage } from '@type-def_iqfu/AiAppraisalReportsType';
import {
  AiAppraisalBuildingStructure,
  AiAppraisalPropertyType,
} from '@type-def_iqfu/chat/AiAppraisalLandPriceEstimation';

import { Prefectures } from '@constants_iqfu';

// ①〜⑳ are the contiguous code points U+2460〜U+2473; later indexes have no circled form
export const circledNumber = (index: number): string =>
  index >= 0 && index < 20 ? String.fromCodePoint(0x2460 + index) : String(index + 1);

// 1坪 = 3.30578㎡
export const SQM_PER_TSUBO = 3.30578;

// Convert yen to man-yen (rounded to the nearest 万円)
export const convertYenToManYen = (yen: number): number => {
  return Math.round(yen / 10000);
};

// Convert yen to man-yen with one decimal place (for unit prices)
export const convertYenToManYenUnitPrice = (yen: number): number => {
  return getFloatNumberRoundValue(yen / 10000, 1);
};

export const AiAppraisalPropertyTypeLabels: Record<AiAppraisalPropertyType, string> = {
  land: '土地',
  house: '戸建て',
  mansion: 'マンション',
};

export const AiAppraisalBuildingStructureLabels: Record<AiAppraisalBuildingStructure, string> = {
  wooden: '木造',
  light_steel: '軽量鉄骨造',
  steel: '鉄骨造',
  rc: '鉄筋コンクリート造',
  src: '鉄骨鉄筋コンクリート造',
  other_building_structures: 'その他',
  cb: 'コンクリートブロック造',
};

export const sellingPlanSimulations = [
  { key: 'challenge', label: 'チャレンジ価格', priceRate: 1.1, expectedPeriod: '6ヶ月~', isRecommended: false },
  { key: 'standard', label: '標準価格', priceRate: 1, expectedPeriod: '3~4ヶ月', isRecommended: true },
  { key: 'speed', label: 'スピード価格', priceRate: 0.9, expectedPeriod: '~2ヶ月', isRecommended: false },
] as const;

// Brokerage fee by the statutory quick formula for prices over 4,000,000 yen: (price * 3% + 60,000 yen) + 10% consumption tax
export const calculateBrokerageFeeYen = (priceYen: number): number => (priceYen * 0.03 + 60000) * 1.1;

export const calculateNetProceedsYen = (priceYen: number): number => priceYen - calculateBrokerageFeeYen(priceYen);

// Each area stat falls back independently (city -> major city -> prefecture -> country) while the API
// returns only the resolved area's name, so the wider levels are derived from the property address.
// A designated city's City record carries its ward (大阪市北区), and the major city it falls back to is its 市 prefix.
export const resolveAreaStatAreaName = (
  areaType: AiAppraisalReportAreaStatType,
  resolvedAreaName: string,
  propertyFullAddress: string,
): string | null => {
  switch (areaType) {
    case 'country':
      return '全国';
    case 'prefecture':
      return Object.values(Prefectures).find((name) => propertyFullAddress.startsWith(name)) ?? null;
    case 'major_city':
      return resolvedAreaName.match(/^(.+市).+区$/)?.[1] ?? resolvedAreaName;
    case 'city':
      return resolvedAreaName;
  }
};

export const countAiAppraisalReportMonthlyUsage = (usage: AiAppraisalReportUsage): number =>
  usage.used_count + usage.in_progress_count;

export const formatAiAppraisalReportUsageCount = (count: number, limit: number): string => `${count}／${limit}回`;

export const buildAiAppraisalReportUsageConfirmMessage = (usage: AiAppraisalReportUsage): string =>
  `今月${countAiAppraisalReportMonthlyUsage(usage) + 1}回目の使用です（残り${Math.max(
    usage.remaining_count - 1,
    0,
  )}回）`;

export const buildAiAppraisalReportLimitReachedMessage = (limit?: number): string =>
  `今月の上限${limit ? `（${limit}回）` : ''}に達しました。翌月1日にリセットされます`;

export const isAiAppraisalReportLimitExceededError = (data: unknown): boolean =>
  Array.isArray(data) &&
  data.some(
    (error) =>
      typeof error === 'object' &&
      error !== null &&
      (error as Record<string, unknown>).monthly_usage === 'limit_exceeded',
  );
